import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import { flowRight } from 'lodash';
import moment from 'moment';
import {
  ELSDropDown,
  ELSDropDownOption,
  ELSIcon
} from '@els/els-component-form-field-react';
import { Line } from 'react-chartjs-2';
import { ELSButton } from '@els/els-component-button-react';
import {
  ELSInlineLoader,
  ELSPageLoader
} from '@els/els-ui-common-react';
import {
  ChatPerformanceDto,
  StudentThemesStatus,
  StudentThemesStatusDto
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import AiChatBaseTemplate from '../../components/ai-chat-base-template/AiChatBaseTemplate.component';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import {
  CHART_HEIGHT,
  N10,
  N7,
  ORANGE
} from './ai-chat-performance.constants';
import 'chartjs-plugin-datalabels';
import {
  ELSButtonSize,
  ELSButtonType
} from '../../models/button.models';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import {
  ELSDataTable,
  ELSTab,
  ELSTabGroup
} from '../../components/els.components';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  fetchAllUserCourseSectionsAction,
  fetchChatPerformanceAction,
  fetchStudentThemesStatusAction,
  postStudentThemesAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import { getBrandName } from '../ai-chat/ai-chat.utilities';
import IsRender from '../../components/is-render/IsRender.component';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';

export type AiChatPerformanceState = {
  selectedCourseSectionId: number;
  performanceDto: ChatPerformanceDto;
  courseSectionOptions: ELSDropDownOption[];
  chart1Width: number;
  isHamburgerMenuOpen: boolean;
  endDateOffset: number;
  themesStatusDtoMap: Record<string, StudentThemesStatusDto>;
  timeoutRef: NodeJS.Timeout;
  activeTabIndex: number;
  // Due to the live announcements, the timing of setting this field
  // must be done before and after use of pollStatus function
  announceThemesStatus: boolean;
};

const defaultState: AiChatPerformanceState = {
  selectedCourseSectionId: null,
  performanceDto: null,
  courseSectionOptions: [],
  chart1Width: null,
  isHamburgerMenuOpen: false,
  endDateOffset: 0,
  themesStatusDtoMap: null,
  timeoutRef: null,
  activeTabIndex: 0,
  announceThemesStatus: false
};

export const AiChatPerformanceComponent = (): JSX.Element => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);

  const [state, setState] = useState<AiChatPerformanceState>({
    ...defaultState,
    selectedCourseSectionId: parseInt(appState.courseSectionId, 10)
  });

  const mergeState = (newState: Partial<AiChatPerformanceState>) => {
    setState((prevState) => {
      return {
        ...prevState,
        ...newState
      };
    });
  };

  const getMapKey = (endDateOffset: number, currentCourseSectionId: number): string => {
    return `${endDateOffset}-${currentCourseSectionId}`;
  };

  const getStartDate = (endDateOffset: number): string => {
    return moment().add((endDateOffset - 6), 'days').startOf('day').toISOString();
  };

  const updatePerformance = (endDateOffset: number, courseSectionsId: number) => {
    fetchChatPerformanceAction({
      appState,
      appStateRef,
      setAppState,
      startDate: getStartDate(endDateOffset),
      numOfDays: 7,
      courseSectionIds: [courseSectionsId]
    }).then((response) => {
      mergeState({
        performanceDto: response
      });
    });
  };

  const pollStatus = (endDateOffset: number, selectedCourseSectionId: number, isTrackComplete: boolean) => {
    fetchStudentThemesStatusAction({
      appState,
      appStateRef,
      setAppState,
      startDate: getStartDate(endDateOffset),
      numOfDays: 7,
      courseSectionIds: [selectedCourseSectionId]
    })
      .then((response) => {
        setState((prevState) => {
          return {
            ...prevState,
            themesStatusDtoMap: {
              ...prevState.themesStatusDtoMap,
              [getMapKey(endDateOffset, selectedCourseSectionId)]: response
            }
          };
        });

        if (response.status === StudentThemesStatus.ERROR || response.status === StudentThemesStatus.NOT_STARTED) {
          return;
        }

        if (response.status === StudentThemesStatus.IN_PROGRESS) {
          mergeState({
            timeoutRef: setTimeout(() => pollStatus(endDateOffset, selectedCourseSectionId, true), 2000),
            // in case user navigates into date range where themes are in progress
            announceThemesStatus: true
          });
          return;
        }

        if (isTrackComplete) {
          trackNewRelicAction({
            appStateRef,
            actionProps: {
              action: NewRelicAnalyticsAction.AI_CHAT_STUDENT_THEMES_RESULTS,
              props: {
                requestHash: state.themesStatusDtoMap[getMapKey(endDateOffset, selectedCourseSectionId)].requestKey,
                size: response.response.chatThemes.length
              }
            }
          });
        }
      });
  };

  const handleInsightsChange = (endDateOffset: number, selectedCourseSectionId: number, isTrackComplete) => {
    if (state.timeoutRef && state.themesStatusDtoMap) {
      clearTimeout(state.timeoutRef);
    }

    mergeState({
      announceThemesStatus: false
    });

    updatePerformance(endDateOffset, selectedCourseSectionId);
    pollStatus(endDateOffset, selectedCourseSectionId, isTrackComplete);
  };

  useEffect(() => {
    handleInsightsChange(state.endDateOffset, state.selectedCourseSectionId, false);

    fetchAllUserCourseSectionsAction({
      appState,
      appStateRef,
      setAppState
    }).then((courseSections) => {
      mergeState({
        courseSectionOptions: courseSections.map((courseSection) => {
          return {
            value: courseSection.id,
            name: courseSection.courseName
          };
        })
      });
    });
  }, []);

  const isEndDateToday = (): boolean => {
    return state.endDateOffset === 0
  };

  const handleTimeFrameChange = (num: number) => {
    if (num > 0 && isEndDateToday()) {
      return;
    }

    mergeState({
      endDateOffset: state.endDateOffset + num,
    });

    handleInsightsChange(state.endDateOffset + num, state.selectedCourseSectionId, false);
  };

  const handleCourseSectionChange = (e, value) => {
    mergeState({
      selectedCourseSectionId: value,
      performanceDto: null
    });

    handleInsightsChange(state.endDateOffset, value, false);
  };

  const handlePostThemesClick = (isRetry: boolean) => {
    postStudentThemesAction({
      appState,
      appStateRef,
      setAppState,
      startDate: getStartDate(state.endDateOffset),
      numOfDays: 7,
      courseSectionIds: [state.selectedCourseSectionId],
    })
      .then(() => {
        setState((prevState) => {
          return {
            ...prevState,
            themesStatusDtoMap: {
              ...prevState.themesStatusDtoMap,
              [getMapKey(state.endDateOffset, state.selectedCourseSectionId)]: {
                request: null,
                requestKey: null,
                status: StudentThemesStatus.IN_PROGRESS,
                response: null
              }
            },
            announceThemesStatus: true
          };
        });
        trackNewRelicAction({
          appStateRef,
          actionProps: {
            action: NewRelicAnalyticsAction.AI_CHAT_STUDENT_THEMES_CLICK,
            props: {
              isRetry
            }
          }
        });

        pollStatus(state.endDateOffset, state.selectedCourseSectionId, true);
      });
  };

  const renderDateRange = () => {

    if (!state?.performanceDto?.perDayMetrics?.length) {
      return null;
    }

    const perDayMetricsStartDate = state.performanceDto.perDayMetrics[0].startDate
    const perDayMetricsEndDate = state.performanceDto.perDayMetrics[state.performanceDto.perDayMetrics.length - 1].startDate

    if (!perDayMetricsStartDate || !perDayMetricsEndDate) {
      return null;
    }

    const startDate = moment(perDayMetricsStartDate);
    const endDate = moment(perDayMetricsEndDate);

    const monthDayFormat = 'MMMM D';
    const yearFormat = 'YYYY';
    if (startDate.isSame(endDate, 'year')) {
      if (startDate.isSame(endDate, 'month')) {
        // eslint-disable-next-line sonarjs/no-nested-template-literals
        return `${startDate.format(monthDayFormat)} - ${endDate.format(`D, ${yearFormat}`)}`;
      }
      // eslint-disable-next-line sonarjs/no-nested-template-literals
      return `${startDate.format(monthDayFormat)} - ${endDate.format(`${monthDayFormat}, ${yearFormat}`)}`;
    }
    // eslint-disable-next-line sonarjs/no-nested-template-literals
    return `${startDate.format(`${monthDayFormat}, ${yearFormat}`)} - ${endDate.format(`${monthDayFormat}, ${yearFormat}`)}`;
  };

  const renderLineChart = () => {

    if (!state.performanceDto || !state.performanceDto.perDayMetrics) {
      return null;
    }

    const perDayMetrics = state.performanceDto.perDayMetrics.map((day) => {
      return {
        ...day,
        label: moment(day.startDate).format('MM/DD')
      };
    });

    const defaultDataSet = {
      pointBackgroundColor: '#fff',
      pointBorderWidth: 1,
      pointHoverRadius: 5,
      pointHoverBorderWidth: 2,
      pointRadius: 5,
      pointHitRadius: 10,
      lineTension: 0,
    };

    const data = {
      labels: perDayMetrics.map((item) => {
        return item.label;
      }),
      datasets: [
        {
          ...defaultDataSet,
          label: 'Student count',
          fill: true,
          lineTension: 0.5,
          backgroundColor: N7,
          borderColor: N7,
          pointRadius: 1,
          pointBorderColor: N7,
          pointHoverBackgroundColor: N7,
          pointHoverBorderColor: N7,
          data: perDayMetrics.map((item) => {
            return item.studentCount;
          })
        },
        {
          ...defaultDataSet,
          label: 'Query count',
          fill: false,
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          pointBorderColor: ORANGE,
          pointHoverBackgroundColor: ORANGE,
          pointHoverBorderColor: ORANGE,
          data: perDayMetrics.map((item) => {
            return item.queryCount;
          })
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false
      },
      scales: {
        xAxes: [{
          gridLines: {
            display: false
          }
        }],
        yAxes: [{
          ticks: {
            stepSize: 100
          }
        }],
      },
      plugins: {
        datalabels: {
          display: (context) => {
            return context.datasetIndex === 1; // only display labels for second dataset
          },
          color: N10,
          align: 'top',
          formatter: (item) => {
            return item;
          }
        }
      }
    };

    return (
      <div className="c-naa-ai-chat-performance__chart">

        <div className="u-els-margin-bottom">
          <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.GUTTERS_2X]}>
            <FlexItem>
              <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.MIDDLE, FlexLayoutModifier.GUTTERS]}>
                <FlexItem>
                  <div className="c-naa-ai-chat-performance__chart-cir" />
                </FlexItem>
                <FlexItem>
                  <span className="u-els-font-size-h4">Queries</span>
                </FlexItem>
              </FlexLayout>
            </FlexItem>
            <FlexItem>
              <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.MIDDLE, FlexLayoutModifier.GUTTERS]}>
                <FlexItem>
                  <div className="c-naa-ai-chat-performance__chart-mtn" />
                </FlexItem>
                <FlexItem>
                  <span className="u-els-font-size-h4">Students</span>
                </FlexItem>
              </FlexLayout>
            </FlexItem>
          </FlexLayout>

        </div>

        <div style={{ width: '100%', height: CHART_HEIGHT }}>
          <Line data={data} options={options} />
        </div>
      </div>
    );
  };

  const renderTable = () => {
    if (!state.performanceDto || !state.performanceDto.perDayMetrics) {
      return null;
    }

    const perDayMetrics = state.performanceDto.perDayMetrics.map((day) => {
      return {
        ...day,
        date: moment(day.startDate).format('MMMM D')
      };
    });

    return (
      <ELSDataTable
        data={perDayMetrics}
        noWrap
        id="instructor-insights-table"
        columns={
          [
            {
              field: "date",
              header: "Date"
            },
            {
              field: "studentCount",
              header: "Student Count"
            },
            {
              field: "queryCount",
              header: "Query Count"
            }
          ]
        }
      >

      </ELSDataTable>
    );
  };

  const getAverageStudentsPerDay = (): number => {
    if (!state.performanceDto || !state.performanceDto.perDayMetrics) {
      return null;
    }

    const total = state.performanceDto.perDayMetrics.reduce((acc, item) => {
      return acc + item.studentCount;
    }, 0);

    return Math.ceil(total / state.performanceDto.perDayMetrics.length);
  };

  const performanceHasResponse = state.performanceDto;
  const performanceHasNoResults = state.performanceDto
    && (!state.performanceDto.perDayMetrics || state.performanceDto.perDayMetrics.every((item) => {
      return item.studentCount === 0 && item.queryCount === 0;
    }));

  const noStudentEngagement = (): boolean => {
    return performanceHasResponse && performanceHasNoResults;
  };

  const handleTabChange = (index: number) => {
    mergeState({
      activeTabIndex: index
    });
  };

  const performanceDisplay = () => {
    return (
      <>
        {noStudentEngagement() && (
          <div>
            No engagement for this week
          </div>
        )}

        {performanceHasResponse && !performanceHasNoResults && (
          <div className="o-els-container o-els-container--3x">
            <ELSTabGroup
              onChangeActiveIndex={handleTabChange}
              activeIndex={state.activeTabIndex}
            >
              <ELSTab title="Chart view">
                {renderLineChart()}
              </ELSTab>
              <ELSTab title="Table view">
                {renderTable()}
              </ELSTab>
            </ELSTabGroup>
          </div>
        )}

        {
          performanceHasResponse && (
            <div className="o-els-container o-els-container--3x">

              <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_3X, FlexLayoutModifier.LEFT]}>
                <FlexItem>

                  <FlexLayout
                    modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.LEFT, FlexLayoutModifier.MIDDLE]}>
                    <FlexItem>
                      <span id="total-queries-per-week-number" className="u-els-font-size-h1">{state.performanceDto.queryCount}</span>
                    </FlexItem>
                    <FlexItem>
                      <div className="c-naa-ai-chat-performance__chart-cir" />
                    </FlexItem>
                  </FlexLayout>
                  <span id="total-queries-per-week-label" className="u-els-font-size-h4">
                    Total Queries / Week
                  </span>
                </FlexItem>
                <FlexItem>

                  <FlexLayout
                    modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.LEFT, FlexLayoutModifier.MIDDLE]}>
                    <FlexItem>
                      <span id="avg-students-per-day-number" className="u-els-font-size-h1">{getAverageStudentsPerDay()}</span>
                    </FlexItem>
                    <FlexItem>
                      <div className="c-naa-ai-chat-performance__chart-mtn" />
                    </FlexItem>
                  </FlexLayout>
                  <span id="avg-students-per-day-label" className="u-els-font-size-h4">Average Students / Day</span>

                </FlexItem>
              </FlexLayout>

            </div>
          )
        }
      </>
    );
  };

  const themesMapKey = getMapKey(state.endDateOffset, state.selectedCourseSectionId);

  const getThemesStatus = (): StudentThemesStatus => {
    return state.themesStatusDtoMap?.[themesMapKey]?.status;
  };

  const isThemesCompleted = getThemesStatus() === StudentThemesStatus.COMPLETED
    && state.themesStatusDtoMap[themesMapKey].response.chatThemes.length > 0;

  const isNewThemesRequest = !noStudentEngagement()
    && getThemesStatus() === StudentThemesStatus.NOT_STARTED;

  const themesDisplay = () => {
    return (
      <>
        <h3>Prompt themes</h3>
        {state.themesStatusDtoMap?.[themesMapKey]
          && !isThemesCompleted
          && (
            <div className="o-els-container c-els-ai-chat__student-themes">
              {(noStudentEngagement()
                  || getThemesStatus() === StudentThemesStatus.INSUFFICIENT_ENTRIES)
                && (
                  <div className="c-els-ai-chat__student-themes-headings">
                    There are not enough valid student queries for {getBrandName(appState)} to find themes
                  </div>
                )}

              {getThemesStatus() === StudentThemesStatus.ERROR
                && (
                  <div>
                    <div className="c-els-ai-chat__student-themes-headings">An error occurred, please try again</div>
                    <ELSButton
                      type={ELSButtonType.PRIMARY}
                      size={ELSButtonSize.SMALL}
                      id="try-again-themes-btn"
                      className="c-els-ai-chat__student-themes-button"
                      onClick={() => handlePostThemesClick(true)}
                    >
                      Try again
                    </ELSButton>
                  </div>
                )}

              {isNewThemesRequest
                && (
                  <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_3X, FlexLayoutModifier.LEFT]}>
                    <FlexItem>
                      <ELSButton
                        type={ELSButtonType.PRIMARY}
                        size={ELSButtonSize.SMALL}
                        id="retrieve-themes-btn"
                        onClick={() => handlePostThemesClick(false)}
                      >
                        Show themes
                      </ELSButton>
                    </FlexItem>
                    <FlexItem>
                      <div className="c-els-ai-chat__student-themes-headings">
                        Results can take a few minutes
                      </div>
                    </FlexItem>
                  </FlexLayout>
                )}

              {getThemesStatus() === StudentThemesStatus.IN_PROGRESS && (
                <div>
                  <div className="c-els-ai-chat__student-themes-headings">
                    Retrieving themes... Results can take a few minutes
                  </div>
                  <ELSInlineLoader />
                </div>
              )}
            </div>
          )}

        {state.themesStatusDtoMap?.[themesMapKey]
          && isThemesCompleted
          && (
            <div>
              {state.themesStatusDtoMap[themesMapKey].response.chatThemes.map((theme) => {
                return (
                  <div key={theme.title}
                    className="o-els-container c-els-ai-chat__student-themes">
                    <div className="c-els-ai-chat__student-themes-headings u-els-bold">
                      {theme.title}
                    </div>
                    <p>{theme.summary}</p>
                  </div>
                );
              })}
            </div>
          )}
      </>
    );
  };

  const getLiveAnnouncement = (): string => {
    if (!state.announceThemesStatus || getThemesStatus() === StudentThemesStatus.NOT_STARTED) {
      return '';
    }

    if (getThemesStatus() === StudentThemesStatus.IN_PROGRESS) {
      return 'Retrieving themes';
    }

    if (getThemesStatus() === StudentThemesStatus.ERROR) {
      return 'An error occurred while retrieving themes';
    }

    if (getThemesStatus() === StudentThemesStatus.INSUFFICIENT_ENTRIES) {
      return `There are not enough valid student queries for ${getBrandName(appState)} to find themes`;
    }

    return 'Themes are available';
  };

  return (
    <>
      <IsRender isRender={appState.pendingRequestCount > 0}>
        <ELSPageLoader />
      </IsRender>
      <AiChatBaseTemplate
        isPanelOpen={false}
        panelContent={null}
      >
        <div className="c-naa-base-page c-naa-ai-chat-performance">
          <div className="c-naa-base-page__header">
            <div className="c-naa-base-page__inner">
              <h3 id="instructor-insights-header">Instructor insights</h3>
            </div>
          </div>
          <div className="c-naa-base-page__body">
            <div className="c-naa-base-page__inner">

              <div className="o-els-container">
                <FlexLayout modifiers={[FlexLayoutModifier.LEFT]}>
                  <FlexItem>
                    <ELSDropDown
                      value={state.selectedCourseSectionId}
                      changeHandler={handleCourseSectionChange}
                      options={state.courseSectionOptions}
                    >
                      Course section
                    </ELSDropDown>
                  </FlexItem>
                </FlexLayout>
              </div>

              <div className="o-els-container o-els-container--2x">

                <div>
                  <FlexLayout modifiers={[
                    FlexLayoutModifier.GUTTERS_2X,
                    FlexLayoutModifier.MIDDLE,
                    FlexLayoutModifier.LEFT,
                    FlexLayoutModifier.WRAP_AT_MOBILE
                  ]}>
                    <FlexItem classes={['u-els-width-1o1@mobile', 'u-els-margin-bottom@mobile']}>
                      <h3>
                        {renderDateRange()}
                      </h3>
                    </FlexItem>
                    <FlexItem>
                      <button
                        type="button"
                        className="u-els-debuttonize u-els-anchorize u-els-font-size-h4"
                        onClick={() => {
                          handleTimeFrameChange(-7);
                        }}
                      >
                        <FlexLayout
                          modifiers={[FlexLayoutModifier.GUTTERS_1o2, FlexLayoutModifier.LEFT, FlexLayoutModifier.MIDDLE]}>
                          <FlexItem>
                            <ELSIcon
                              prefix="hmds"
                              name="chevron-left"
                              align="middle"
                              size="1x"
                            />
                          </FlexItem>
                          <FlexItem>
                            Previous Week
                          </FlexItem>
                        </FlexLayout>
                      </button>
                    </FlexItem>
                    <FlexItem isRender={!isEndDateToday()}>
                      <button
                        type="button"
                        className="u-els-debuttonize u-els-anchorize u-els-font-size-h4"
                        onClick={() => {
                          handleTimeFrameChange(7);
                        }}
                      >
                        <FlexLayout
                          modifiers={[FlexLayoutModifier.GUTTERS_1o2, FlexLayoutModifier.RIGHT, FlexLayoutModifier.MIDDLE]}>
                          <FlexItem>
                            Next Week
                          </FlexItem>
                          <FlexItem>
                            <ELSIcon
                              prefix="hmds"
                              name="chevron-right"
                              align="middle"
                              size="1x"
                            />
                          </FlexItem>
                        </FlexLayout>
                      </button>
                    </FlexItem>
                  </FlexLayout>
                </div>
              </div>

              {performanceDisplay()}

              {
                performanceHasResponse && (themesDisplay())
              }

            </div>
          </div>

          <span className="u-els-hide-visually" aria-live="assertive">
            {getLiveAnnouncement()}
          </span>

        </div>

      </AiChatBaseTemplate>
    </>
  );
};

const enhance = flowRight(
  withHTMLHeadSEO({ title: 'Instructor insights' }),
);

const AiChatPerformance = enhance(AiChatPerformanceComponent);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default AiChatPerformance;
